import { Component } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import * as option from "@/models/configurator/configurator_options";
import { EventBus } from "@/event-bus";
import { FabricSwatch, FixedFabricArray } from "@/models/products/fabric";
import { LayoutStore, ShapeStore, CanopyStore } from "@/mixins/store";
import FabricService from "@/services/fabric_service";
import { StockMixin } from "@/mixins/stock";
import { FabricMixin } from "@/mixins/fabric";
import { ProductLineName } from "@/models/products/collection";
import { fabricTextureKey } from "@/helpers/fabric";

@Component
export class FabricRules extends mixins(LayoutStore, ShapeStore, CanopyStore, StockMixin, FabricMixin) {
  protected Preset = option.Preset;
  //protected allowPocketFabric = false;
  // protected allowVentFabric = false;
  // protected allowValanceFabric = false;
  protected whiteFabric: null | FabricSwatch = null;
  protected whpFabric: null | FabricSwatch = null;
  protected fabricService = new FabricService();
  protected hasValanceBindingOverride = false;

  protected async getWhpFabric(): Promise<FabricSwatch> {
    if (!this.whpFabric) {
      this.whpFabric = await this.fabricService.getSingleFabric("WHP");
    }
    return this.whpFabric;
  }

  protected async initFabricRules(): Promise<void> {
    this.allowVent(false);
    this.allowValance(false);
    /** Ready waits for the configurator to fire off event bus; we want to make sure it's loaded before we run methods on the viewer */
    EventBus.$on("ready", () => this.readyCanopy);
    /** The changeXXX methods toggle whether the namesake component should sync with the main canopy fabric (changeVent = true means the vent fabric CAN CHANGE independently of the main canopy)
     * If the user explicity changes the fabric of the namesake component, the boolean will be set to true and the component fabric will no longer sync with the main canopy
     * Applies to vent (both top & middle), pocket & valance
     */
    await this.setWhiteFabric();
    //retaining changeXXXX methods when loader is first initialized. if the fabric for each component does not match the main canopy, then turn off auto changing when main canopy fabric changes
    if (!this.isStockOnly) {
      const main = this.canopyFabric.mfr_code;
      // Top vent diverges from main canopy
      if (this.collection.handle !== ProductLineName.Nova && this.ventTopFabric[0].mfr_code !== main) {
        this.allowVent(true);
      }
      // Middle vent (double vent) fabrics diverge from main canopy panels — preserve user selections
      if (this.ventMiddleFabric && this.ventMiddleFabric.length > 0) {
        const middleDiverges = this.ventMiddleFabric.some((mvFabric: FabricSwatch, idx: number) => {
          const correspondingMain = this.mainFabric[idx];
          return !correspondingMain || mvFabric.mfr_code !== correspondingMain.mfr_code;
        });
        if (middleDiverges) {
          this.allowVent(true);
        }
      }
      // Valance diverges from main canopy panels across any slot — preserve
      // user selections (including 3- or 4-fabric alternating valances where
      // only slots 2+ differ from the main canopy).
      if (this.valanceFabric && this.valanceFabric.length > 0) {
        const valanceDiverges = this.valanceFabric.some((vFabric: FabricSwatch, idx: number) => {
          const correspondingMain = this.mainFabric[idx];
          return !correspondingMain || vFabric.mfr_code !== correspondingMain.mfr_code;
        });
        if (valanceDiverges) {
          this.allowValance(true);
        }
      }
    }
  }

  protected async setWhiteFabric(): Promise<void> {
    let whiteFabricMfrCode = '';
    switch (this.canopyFabric.weight) {
      case ('8') :
        whiteFabricMfrCode = '57003-0000';
        break;
      case ('9') :
        whiteFabricMfrCode = 'R-099';
        break;
    }
    const res = await this.fabricService.getSingleFabric(whiteFabricMfrCode);
    this.whiteFabric = res;
  }

  beforeDestroy() {
    EventBus.$off("ready");
  }

  // protected async ribFabricRules(): Promise<void> {
  //   if (this.rib === option.Rib.Pockets) {
  //     const stripes = this.checkForStripes(this.ribFabric!);
  //     if (stripes) {
  //       this.addRibFabric(this.whiteFabric);
  //       await this.$viewer.ChangeRibAttachment(
  //         option.Rib.Pockets,
  //         this.whiteFabric?.mfr_code
  //       );
  //     }
  //   } else {
  //     this.addRibFabric(null);
  //   }
  // }

  /**
   * Called from watcher in Designer.vue
   *  Auto-toggle items based on value of fringe (ex: set the default fringe fabric when a user selects a fringe type)
   */
  protected async fringeFabricRules(): Promise<void> {
    // if fringe is selected, set the default fringe fabric to WHP
    if (
      this.fringe !== option.Fringe.None &&
      (this.fringeFabric as any).length === 0
    ) {
      const whp = await this.getWhpFabric();
      await this.addFringeFabric([whp]);
    }
  }

  /**
   * Called from watcher in Designer.vue
   *  Auto-toggle items based on value of valances (ex: clear valance binding fabric & valance fabric if user removes valance)
   */
  protected async valanceFabricRules(): Promise<void> {
    if (this.valance === option.Valance.Canopy) {
      await this.$viewer.ChangeBindingFabric(null);
      this.addBindingFabric(null);
      this.addValanceFabric([]);
      //await this.$viewer.ChangeValanceFabric(null, null, null, null); commenting this in bugs the valance
    } else {
      const whp = await this.getWhpFabric();
      const binding =
        this.fringeFabric[0] && !this.hasValanceBindingOverride
          ? this.fringeFabric[0]
          : this.hasValanceBindingOverride && this.bindingFabric
            ? this.bindingFabric
            : whp;
      this.addBindingFabric(binding);
      await this.$viewer.ChangeBindingFabric(fabricTextureKey(binding!));
      // Valance length is independent of main canopy panel count. Preserve
      // whatever the user had selected. When first enabling valance (empty
      // array), seed it from the main canopy — copying all alternating
      // fabrics so a 4-panel main canopy produces a 4-fabric valance.
      const valanceLen = (this.valanceFabric as any).length;
      if (valanceLen === 0) {
        const seed = (this.mainFabric as any).slice() as FabricSwatch[];
        this.addValanceFabric((seed as unknown) as FixedFabricArray);
        await this.$viewer.ChangeValanceFabric(
          seed[0].fabric_scale,
          fabricTextureKey(seed[0]),
          seed[1] ? seed[1].fabric_scale : undefined,
          seed[1] ? fabricTextureKey(seed[1]) : undefined,
          seed[2] ? seed[2].fabric_scale : undefined,
          seed[2] ? fabricTextureKey(seed[2]) : undefined,
          seed[3] ? seed[3].fabric_scale : undefined,
          seed[3] ? fabricTextureKey(seed[3]) : undefined
        );
      } else if (valanceLen === 1) {
        const swatch = this.valanceFabric[0];
        await this.$viewer.ChangeValanceFabric(swatch.fabric_scale, fabricTextureKey(swatch));
        this.addValanceFabric([swatch]);
      } else {
        const swatch = this.valanceFabric[0];
        const swatchAlt = this.valanceFabric[1];
        const swatchAltSecond = valanceLen > 2 ? this.valanceFabric[2] : undefined;
        const swatchAltThird = valanceLen > 3 ? this.valanceFabric[3] : undefined;
        const valanceFabrics = [swatch, swatchAlt];
        if (swatchAltSecond) valanceFabrics.push(swatchAltSecond);
        if (swatchAltThird) valanceFabrics.push(swatchAltThird);
        this.addValanceFabric((valanceFabrics as unknown) as FixedFabricArray);
        await this.$viewer.ChangeValanceFabric(
          swatch.fabric_scale,
          fabricTextureKey(swatch),
          swatchAlt.fabric_scale,
          fabricTextureKey(swatchAlt),
          swatchAltSecond ? swatchAltSecond.fabric_scale : undefined,
          swatchAltSecond ? fabricTextureKey(swatchAltSecond) : undefined,
          swatchAltThird ? swatchAltThird.fabric_scale : undefined,
          swatchAltThird ? fabricTextureKey(swatchAltThird) : undefined
        );
      }
    }
  }

  /**
   * Called from watcher in Designer.vue
   * See above EventBus methods in initFabricRules() method for more info
   * Syncs pocket, valance, and/or vent fabric to canopy fabric automatically depending on user action
   * */
  protected async canopyFabricRules(): Promise<void> {
    const swatch = this.canopyFabric;
    const swatchAlt = this.mainFabric[1] ? this.mainFabric[1] : swatch;
    // if (!this.allowPocketFabric && this.canopyFabric) {
    //   const stripes = this.checkForStripes(swatch);
    //   if (stripes) {
    //     this.addRibFabric(this.whiteFabric);
    //     await this.$viewer.ChangeRibAttachment(this.rib, this.whiteFabric!.mfr_code);
    //   } else {
    //     this.addRibFabric(swatch);
    //     await this.$viewer.ChangeRibAttachment(this.rib, swatch.mfr_code);
    //   }
    // }

    /* FABRIC WEIGHT LOGIC BELOW */
    // Alternating Main Canopy Panels
    if (swatchAlt !== swatch && swatchAlt.weight !== swatch.weight) {
      this.addMainFabric([swatch, swatch]);
      await this.$viewer.ChangeCanopyFabric(
        swatch.fabric_scale,
        fabricTextureKey(swatch)
      );
    }
    // Top Vent...this also sets the foundation of what's needed to allow for the examination of the middle vent fabrics
    if (
      this.ventTopFabric.length > 0 &&
      this.ventTopFabric[0].weight &&
      this.ventTopFabric[0].weight !== swatch.weight
    ) {
      this.allowVent(false);
    }
    // Valance
    if (this.valance !== option.Valance.Canopy) {
      if (
        this.valanceFabric[0] && this.valanceFabric[0].weight !== swatch.weight ||
        this.valanceFabric[1] && this.valanceFabric[1].weight !== swatch.weight
      ) {
        this.allowValance(false);
      }
    }

    if (!this.allowVentFabric && this.canopyFabric) {
      this.addVentTopFabric([swatch]);
      await this.$viewer.ChangeTopVentFabric(swatch.fabric_scale, fabricTextureKey(swatch));
      if (this.isDoubleVent && this.ventMiddle === option.Panel.Alternating) {
        const swatchAlt2 = this.mainFabric[2] ? this.mainFabric[2] : undefined;
        const swatchAlt3 = this.mainFabric[3] ? this.mainFabric[3] : undefined;
        if (swatchAlt3) {
          this.addVentMiddleFabric([swatch, swatchAlt, swatchAlt2!, swatchAlt3]);
        } else if (swatchAlt2) {
          this.addVentMiddleFabric([swatch, swatchAlt, swatchAlt2]);
        } else {
          this.addVentMiddleFabric([swatch, swatchAlt]);
        }
        await this.$viewer.ChangeDblVentFabric(
          swatch.fabric_scale,
          fabricTextureKey(swatch),
          swatchAlt.fabric_scale,
          fabricTextureKey(swatchAlt),
          swatchAlt2 ? swatchAlt2.fabric_scale : undefined,
          swatchAlt2 ? fabricTextureKey(swatchAlt2) : undefined,
          swatchAlt3 ? swatchAlt3.fabric_scale : undefined,
          swatchAlt3 ? fabricTextureKey(swatchAlt3) : undefined
        );
      } else if (this.isDoubleVent && this.ventMiddle === option.Panel.Solid) {
        this.addVentMiddleFabric([swatch]);
        await this.$viewer.ChangeDblVentFabric(
          swatch.fabric_scale,
          fabricTextureKey(swatch),
          swatch.fabric_scale,
          fabricTextureKey(swatch)
        );
      }
    }
    if (this.valance !== option.Valance.Canopy && !this.allowValanceFabric) {
      // Valance mirrors the full main canopy array (both fabrics AND length)
      // until the user customizes any valance fabric, at which point
      // allowValanceFabric becomes true and valance becomes independent.
      const synced = (this.mainFabric as any).slice() as FabricSwatch[];
      this.addValanceFabric((synced as unknown) as FixedFabricArray);
      await this.$viewer.ChangeValanceFabric(
        synced[0].fabric_scale,
        fabricTextureKey(synced[0]),
        synced[1] ? synced[1].fabric_scale : undefined,
        synced[1] ? fabricTextureKey(synced[1]) : undefined,
        synced[2] ? synced[2].fabric_scale : undefined,
        synced[2] ? fabricTextureKey(synced[2]) : undefined,
        synced[3] ? synced[3].fabric_scale : undefined,
        synced[3] ? fabricTextureKey(synced[3]) : undefined
      );
    }
  }

  /**
   *
   * @param {FabricSwatch} swatch
   * @returns {boolean} true if swatch is striped
   */
  protected checkForStripes(swatch: FabricSwatch): boolean {
    let stripes = false;
    if (swatch.pattern_group && swatch.pattern_group.length > 0) {
      const patterns = swatch.pattern_group;
      if (patterns.includes("Stripes")) {
        stripes = true;
      }
    }
    return stripes;
  }

  protected readyCanopy(): void {
    //fabric rules on configurated umbrellas
    // if (this.canopyFabric.mfr_code !== this.ribFabric?.mfr_code) {
      //   this.allowPocketFabric = true;
      // }
    if (
      (this.ventTopFabric[0] &&
        this.canopyFabric.mfr_code !== this.ventTopFabric[0].mfr_code) ||
      (this.isDoubleVent &&
        this.canopyFabric.mfr_code !== this.ventMiddleFabric[0].mfr_code)
    ) {
      this.allowVent(true);
    }
  }

  protected get canopyFabric(): FabricSwatch {
    return this.mainFabric[0];
  }

  protected get isDoubleVent(): boolean {
    return this.ventMiddleFabric.length > 0;
  }

  /**
   * Intended to test designer layouts for striped fabrics, which are disallowed
   *
   * @returns {boolean} true if any prohibited area has striped fabric
   */
  protected async checkModelForStripes(): Promise<boolean> {
    let stripes = false;
    const fabrics = [
      ...this.mainFabric,
      ...this.ventTopFabric,
      ...this.ventMiddleFabric
    ];
    for (const swatch of fabrics) {
      if (swatch && this.checkForStripes(swatch)) {
        stripes = true;
        break;
      }
    }
    return stripes;
  }

  protected async toggleStripeWarning(): Promise<void> {
    EventBus.$emit("toggleStripesModal");
  }
}
